import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Upload, FileSpreadsheet, Download, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportQuestionnaires = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Seleziona un file Excel (.xlsx o .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Seleziona un file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.post(`${API}/questionnaires/import-excel`, formData, {
        headers,
      });

      setResult(response.data);
      toast.success(response.data.message);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setFile(null);
        setResult(null);
      }, 5000);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'upload del file');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create Excel template structure
    const templateData = [
      ['titolo', 'data_completamento', ...Array.from({ length: 30 }, (_, i) => `domanda_${i + 1}`)],
      ['Valutazione Esempio', '2026-01-15', ...Array(30).fill(2)],
    ];

    const csvContent =
      'data:text/csv;charset=utf-8,' + templateData.map((row) => row.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'template_questionari.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();

    toast.success('Template scaricato. Aprilo con Excel e compila i dati.');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-[#3BA99C] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Dashboard
        </button>
      </div>

      <div>
        <h1 className="text-4xl font-bold text-slate-900">Import Questionari da Excel</h1>
        <p className="text-slate-500 mt-2">
          Carica un file Excel con i questionari completati
        </p>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Istruzioni
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Formato File Excel</h3>
              <p className="text-sm text-slate-600 mb-3">
                Il file deve contenere le seguenti colonne:
              </p>
              <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-4">
                <li>
                  <strong>titolo</strong>: Titolo del questionario
                </li>
                <li>
                  <strong>data_completamento</strong>: Data nel formato YYYY-MM-DD (es: 2026-01-15)
                </li>
                <li>
                  <strong>domanda_1 a domanda_30</strong>: Risposte numeriche da 0 a 4
                  <ul className="list-circle list-inside ml-6 mt-1">
                    <li>0 = Non implementato</li>
                    <li>1 = Carente</li>
                    <li>2 = Accettabile</li>
                    <li>3 = Buono</li>
                    <li>4 = Eccellente</li>
                  </ul>
                </li>
              </ul>
            </div>

            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-[#21358E] hover:bg-[#1a2a70] text-white rounded-lg transition-colors"
            >
              <Download className="w-5 h-5" />
              Scarica Template CSV
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Carica File</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-[#3BA99C] transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <Upload className="w-12 h-12 text-slate-400" />
                <div>
                  <p className="text-slate-700 font-medium">
                    {file ? file.name : 'Clicca per selezionare un file Excel'}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Supportati: .xlsx, .xls</p>
                </div>
              </label>
            </div>

            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full bg-[#3BA99C] hover:bg-[#2D8A7F] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Caricamento...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Carica e Importa
                </>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {result.imported > 0 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              Risultati Import
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-lg font-semibold text-slate-900">{result.message}</p>
              
              {result.errors && result.errors.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-semibold text-red-600 mb-2">Errori:</h3>
                  <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                    {result.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                  {result.errors.length === 10 && (
                    <p className="text-xs text-slate-500 mt-2 italic">
                      (Mostrando primi 10 errori)
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportQuestionnaires;